import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { List } from 'immutable';
import { forkJoin, of, Subject } from 'rxjs';
import { v4 } from 'uuid';
import { Document } from '../../../document-data/document.model';
import { CalculationResult } from '../../../document-data/sub-models/calculation-result.model';
import { DocumentItem } from '../../../document-data/sub-models/document-item.model';
import { ItemCalculationResult } from '../../../document-data/sub-models/item-calculation-result.model';
import { CompanySettings } from '../../../settings-data/sub-models/company-settings/company-settings.model';
import { Tax } from '../../../settings-data/sub-models/company-settings/tax.model';
import { WithholdingTax } from '../../../settings-data/sub-models/company-settings/withholding-tax.model';
import { DocumentPresetSettings } from '../../../settings-data/sub-models/document-preset-settings/document-preset-settings.model';
import {
  ITrackingObject,
  TrackingScreenNamesEnum,
} from '../../../tracking/tracking.data';
import { filter, take, takeUntil } from 'rxjs/operators';
import { IDocumentDataBuilder } from '../../../document-data/document-data-builder.interface';

interface IDocumentItemCalculationResult {
  item: DocumentItem;
  calculationResult: ItemCalculationResult;
}

export interface IDocumentItemReorderInfo {
  oldIndex: number;
  newIndex: number;
  isPart: boolean;
}

@Component({
  selector: 'app-document-items-list',
  templateUrl: 'document-items-list.component.html',
  styleUrls: ['document-items-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DocumentItemsListComponent
  implements OnInit, OnChanges, OnDestroy {
  @Input() documentItems: List<DocumentItem>;
  @Input() documentCalculationResult: CalculationResult;
  @Input() document: Document;
  @Input() documentTaxes: List<Tax>;
  @Input() documentWithholdingTax: WithholdingTax;
  @Input() documentDataBuilder: IDocumentDataBuilder;
  @Input() documentSettings: DocumentPresetSettings;
  @Input() companySettings: CompanySettings;
  @Input() enableDiscount: boolean;
  @Input() activeItemReferenceId: string;
  @Input() trackingObject: ITrackingObject;
  @Input() trackingScreenName: TrackingScreenNamesEnum;

  @Output()
  onDeleteDocumentItem: EventEmitter<DocumentItem> = new EventEmitter<DocumentItem>();
  @Output()
  onSaveDocumentItem: EventEmitter<DocumentItem> = new EventEmitter<DocumentItem>();
  @Output() onEditMode: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output()
  onSaveItemToList: EventEmitter<DocumentItem> = new EventEmitter<DocumentItem>();
  @Output()
  onReorderDocumentItem: EventEmitter<IDocumentItemReorderInfo> = new EventEmitter<IDocumentItemReorderInfo>();

  private _destroyed: Subject<boolean> = new Subject<boolean>();

  documentItemCalculatedResults: List<IDocumentItemCalculationResult>;
  sortableJsOptions: any;
  reorderingMode: boolean;
  editMode: boolean;
  sortableHandle: string;
  editingDocumentItemId: string;

  constructor(private _changeDetectorRef: ChangeDetectorRef) {}

  ngOnChanges(changes) {
    if (changes.documentItems || changes.documentCalculationResult) {
      if (this.documentItems && this.documentCalculationResult) {
        this.documentItemCalculatedResults = this.documentItems.map(
          (documentItem: DocumentItem) => {
            return {
              item: documentItem,
              calculationResult: this.documentCalculationResult.items.find(
                (itemCalculationResult) =>
                  itemCalculationResult.itemId === documentItem.id
              ),
            };
          }
        ) as List<IDocumentItemCalculationResult>;
      }
    }

    if (
      changes.activeItemReferenceId &&
      this.activeItemReferenceId &&
      this.documentItems
    ) {
      this.triggerEditMode(this.activeItemReferenceId, this.documentItems);
    }
  }

  ngOnInit() {
    this.sortableHandle = 'sortable-handle';
    this.sortableJsOptions = {
      group: v4(),
      filter: '.document-item-list-item-wrapper.edit-mode',
      preventOnFilter: false,
      fallbackTolerance: 3,
      scrollSensitivity: 50,
      scrollSpeed: 40,
      handle: `.${this.sortableHandle}`,
      onStart: () => {
        this.reorderingMode = true;

        this._changeDetectorRef.markForCheck();
      },
      onEnd: () => {
        this.reorderingMode = false;

        this._changeDetectorRef.markForCheck();
      },
      onUpdate: (evt: any) => {
        this.onReorderDocumentItem.emit({
          oldIndex: evt.oldIndex,
          newIndex: evt.newIndex,
          isPart: this.documentItems.get(evt.oldIndex).isPart(),
        });
      },
    };

    // trigger edit mode based on initialReferenceId
    forkJoin(
      of(this.activeItemReferenceId).pipe(filter((x) => !!x)),
      of(this.documentItems).pipe(filter((x) => !!x))
    )
      .pipe(takeUntil(this._destroyed), take(1))
      .subscribe(([referenceId, documentItems]) =>
        this.triggerEditMode(referenceId, documentItems)
      );
  }

  triggerEditMode(referenceId: string, documentItems: List<DocumentItem>) {
    const editingItem = documentItems.find(
      (documentItem: DocumentItem) => documentItem.reference.id === referenceId
    );

    if (editingItem) {
      // set timeout so list-items have been created before entering in edit mode
      setTimeout(() => {
        this.onDocumentItemEditMode({
          documentItemId: editingItem.id,
          editMode: true,
        });
      });
    }
  }

  onDocumentItemEditMode({
    documentItemId,
    editMode,
  }: {
    documentItemId: string;
    editMode: boolean;
  }) {
    this.onEditMode.emit(editMode);

    this.editingDocumentItemId = editMode ? documentItemId : null;
    this.editMode = editMode;

    this._changeDetectorRef.markForCheck();
  }

  trackByFn(index: number, documentItem: IDocumentItemCalculationResult) {
    return documentItem.item.id;
  }

  ngOnDestroy() {
    this._destroyed.next(true);
    this._destroyed.unsubscribe();
  }
}
